import { Injectable } from '@angular/core';
import { TreeNode, UUID } from '@s8l/client-tree-lib';

import {
  Achievement,
  AchievementDisplay,
  AchievementFilter,
  AchievementNotificationEffect,
  AchievementStat,
  AchievementStatEffect,
  CmsServerService
} from './jsonrpc/cms-server.service';
import { CmsService } from './cms.service';

export interface JourneyBase {
  uuid?: string;
  children: JourneyElement[];
  achievement?: Achievement;
}

export interface JourneyElement extends JourneyBase {
  node?: TreeNode;
}

export interface Journey extends JourneyBase {
  name: string;
  filters: AchievementFilter[];
}

@Injectable({
  providedIn: 'root'
})
export class JourneyService {
  constructor(private cms: CmsService, private server: CmsServerService) {}

  public list(): Promise<Journey[]> {
    return this.server.achievementList().then(list => list.filter(a => a.type === 'journey').map(a => this.toJourney(a)));
  }

  public delete(journey: Journey): Promise<any> {
    return this.server.achievementDelete(journey.uuid, true, true);
  }

  public upsert(journey: Journey): Promise<Journey> {
    const achievement = this.toAchievementElement(journey.name, journey, null);
    achievement.name = journey.name;
    achievement.filters = journey.filters;
    return this.server.achievementUpsert([achievement]).then(res => this.toJourney(res[0]));
  }

  private toAchievementElement(baseName: string, element: JourneyElement, parent: string): Achievement {
    const uuid = element.uuid ?? UUID.random();
    const children: Achievement[] = element.children?.map(c => this.toAchievementElement(baseName, c, uuid)) ?? [];
    const effects: (AchievementNotificationEffect | AchievementStatEffect)[] = [{ type: 'notification' }];

    let stat: AchievementStat;
    let unlock: number;

    if (children.length > 0) {
      // Parent element: Unlock this achievement when all children are achieved
      stat = { event: 'journey', selector: uuid, max_value: children.length };
      unlock = children.length;
    } else {
      // Leaf element: Unlock this achievement when the user spend time in this node
      stat = { event: 'page-time', selector: element.node.uuid, max_value: 1 };
      unlock = 1;
    }

    if (parent != null) {
      // Child element: Increment counter stat on parent
      effects.push({ type: 'stat_add', event: 'journey', selector: parent, value: 1, increment: true });
    }

    const achievement: Achievement = {
      uuid,
      stat,
      children,
      name: null,
      type: 'journey',
      display: AchievementDisplay.Show,
      meta: { node: element.node?.uuid, ...element.achievement?.meta },
      repeatable: false,
      filters: [],
      effects,
      unlock_value: unlock,
      i18n_locked: {},
      i18n_achieved: {}
    };
    return achievement;
  }

  private toJourney(a: Achievement): Journey {
    const e = this.toJourneyElement(a);
    return {
      ...e,
      name: a.name,
      filters: a.filters
    };
  }

  private toJourneyElement(a: Achievement): JourneyElement {
    const node = this.cms.tree.value.getByUUID(a.meta?.node);
    const children = a.children.map(c => this.toJourneyElement(c)).filter(c => c != null);
    return {
      uuid: a.uuid,
      node,
      children,
      achievement: a
    };
  }
}
